import { useMemo } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { AlertTriangle, Clock } from 'lucide-react';
import { cn } from 'lib/utils';
import type { IOrg } from 'modules/orgs/interfaces';
import { OrderStatusEnum, type Order } from '../lib/types';

const DAY_MS = 24 * 60 * 60 * 1000;
const NEXT_EXPECTED_DELIVERY_WARNING_WINDOW_DAYS = 7;
function formatDate(value?: Date) {
  return value ? value.toLocaleDateString('da-DK') : 'Ikke beregnet';
}
type ManualNextExpectedDeliveryAlert = {
  order: Order;
  companyName: string;
  alertKind: 'due' | 'soon';
};

function getManualNextExpectedDeliveryAlerts({
  orders,
  orgMap,
  now = new Date(),
}: {
  orders: Order[];
  orgMap: Record<string, IOrg>;
  now?: Date;
}): ManualNextExpectedDeliveryAlert[] {
  const warningThreshold = new Date(
    now.getTime() + NEXT_EXPECTED_DELIVERY_WARNING_WINDOW_DAYS * DAY_MS
  );
  const latestOrdersByOrg = orders.reduce((acc, order) => {
    if (order.status !== OrderStatusEnum.COMPLETED) {
      return acc;
    }

    const existingOrder = acc[order.orgId];
    if (!existingOrder || order.createTime.getTime() > existingOrder.createTime.getTime()) {
      acc[order.orgId] = order;
    }

    return acc;
  }, {} as Record<string, Order>);

  return Object.values(latestOrdersByOrg)
    .filter((order) => {
      if (!order.nextExpectedDeliveryDate) {
        return false;
      }

      return order.nextExpectedDeliveryDate.getTime() <= warningThreshold.getTime();
    })
    .map(
      (order): ManualNextExpectedDeliveryAlert => ({
        order,
        companyName: orgMap[order.orgId]?.title || order.companyName || 'Ordre',
        alertKind:
          order.nextExpectedDeliveryDate &&
          order.nextExpectedDeliveryDate.getTime() <= now.getTime()
            ? 'due'
            : 'soon',
      })
    )
    .sort((a, b) => {
      if (a.alertKind !== b.alertKind) {
        return a.alertKind === 'due' ? -1 : 1;
      }

      const aTime = a.order.nextExpectedDeliveryDate?.getTime() ?? Number.POSITIVE_INFINITY;
      const bTime = b.order.nextExpectedDeliveryDate?.getTime() ?? Number.POSITIVE_INFINITY;
      if (aTime !== bTime) {
        return aTime - bTime;
      }

      return a.companyName.localeCompare(b.companyName, 'da');
    });
}

function ManualNextExpectedDeliverySection({
  alerts,
  onSelectOrder,
}: {
  alerts: ManualNextExpectedDeliveryAlert[];
  onSelectOrder?: (order: Order) => void;
}) {
  if (alerts.length === 0) {
    return null;
  }

  const dueAlerts = alerts.filter((alert) => alert.alertKind === 'due');
  const soonAlerts = alerts.filter((alert) => alert.alertKind === 'soon');

  const renderAlertRows = (items: ManualNextExpectedDeliveryAlert[]) => (
    <div className="space-y-2">
      {items.map(({ order, companyName, alertKind }) => (
        <button
          key={order.id}
          type="button"
          className={cn(
            'flex w-full flex-col gap-1 rounded-md border bg-white px-3 py-2 text-left transition-colors hover:bg-slate-50 md:flex-row md:items-center md:justify-between',
            alertKind === 'due' ? 'border-red-100' : 'border-slate-200'
          )}
          onClick={() => onSelectOrder?.(order)}
          data-testid={`manual-next-delivery-card-${order.orgId}`}
        >
          <span className="font-medium text-slate-900">{companyName}</span>
          <span className={cn('text-sm', alertKind === 'due' ? 'text-red-600' : 'text-slate-600')}>
            {formatDate(order.nextExpectedDeliveryDate)}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <section className="mb-4 space-y-3" data-testid="manual-next-delivery-section">
      <Accordion type="multiple" className="space-y-3">
        {dueAlerts.length > 0 ? (
          <AccordionItem value="overdue" className="rounded-lg border border-red-300 bg-white px-4">
            <AccordionTrigger className="py-3 text-sm font-normal text-red-600 hover:no-underline">
              <span className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Overskredet forventet leverance dato ({dueAlerts.length})</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3">{renderAlertRows(dueAlerts)}</AccordionContent>
          </AccordionItem>
        ) : null}
        {soonAlerts.length > 0 ? (
          <AccordionItem
            value="upcoming"
            className="rounded-lg border border-slate-200 bg-white px-4"
          >
            <AccordionTrigger className="py-3 text-sm font-normal text-slate-900 hover:no-underline">
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Kommende forventede leverance dato ({soonAlerts.length})</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3">{renderAlertRows(soonAlerts)}</AccordionContent>
          </AccordionItem>
        ) : null}
      </Accordion>
    </section>
  );
}

export function OrderManualNextExpectedDeliveryAlerts({
  orders,
  orgMap,
  onSelectOrder,
}: {
  orders: Order[];
  orgMap: Record<string, IOrg>;
  onSelectOrder?: (order: Order) => void;
}) {
  const manualNextExpectedDeliveryAlerts = useMemo(
    () =>
      getManualNextExpectedDeliveryAlerts({
        orders,
        orgMap,
      }),
    [orders, orgMap]
  );

  return (
    <ManualNextExpectedDeliverySection
      alerts={manualNextExpectedDeliveryAlerts}
      onSelectOrder={onSelectOrder}
    />
  );
}
